import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { FaEye, FaEdit, FaTrash, FaCog } from "react-icons/fa";
import { Search as SearchIcon } from "@mui/icons-material";
import { 
  TextField, 
  InputAdornment, 
  Card, 
  Box, 
  Pagination, 
  FormControlLabel, 
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Button
} from "@mui/material";
import { FiMaximize2, FiX } from "react-icons/fi";
import notFoundImg from "assets/not_found.png";
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiAvatar from "components/VuiAvatar";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import JourneyDetailsModal from "./components/JourneyDetailsModal";
import JourneySettingsModal from "./components/JourneySettingsModal";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const API_URL = process.env.REACT_APP_API_URL || "https://api.csc-taxi.tech";

// Helper function to format status text
const formatStatus = (status) => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to convert display status to API status
const getStatusValue = (displayStatus) => {
  return displayStatus.replace(/\s+/g, '_');
};

// Helper function to convert API status to display status
const getStatusDisplay = (apiStatus) => {
  return apiStatus.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

function Journey() {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [mapRef] = useState(useRef(null));
  const [selectedJourneys, setSelectedJourneys] = useState(new Set());
  const [highlightedJourney, setHighlightedJourney] = useState(null);
  const [selectedJourneyPath, setSelectedJourneyPath] = useState(null);
  const [showPaths, setShowPaths] = useState({
    supposed: true,
    arriving: true,
    taken: true,
  });
  const [showSettings, setShowSettings] = useState(false);

  // New filter states
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedCars, setSelectedCars] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [estimatedPriceMin, setEstimatedPriceMin] = useState('');
  const [estimatedPriceMax, setEstimatedPriceMax] = useState('');
  const [clientPriceMin, setClientPriceMin] = useState('');
  const [clientPriceMax, setClientPriceMax] = useState('');
  const [driverPriceMin, setDriverPriceMin] = useState('');
  const [driverPriceMax, setDriverPriceMax] = useState('');

  useEffect(() => {
    fetchJourneys();
    fetchFilterData();
  }, [search, page, selectedCars, selectedDrivers, selectedClients, selectedStatus, startDate, endDate, startTime, endTime, 
      estimatedPriceMin, estimatedPriceMax, clientPriceMin, clientPriceMax, driverPriceMin, driverPriceMax]);

  const fetchFilterData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Fetch cars
      const carsResponse = await fetch(`${API_URL}/api/admin/cars`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });
      if (carsResponse.ok) {
        const carsData = await carsResponse.json();
        console.log('Cars data:', carsData);
        if (carsData.status === 1) {
          const formattedCars = carsData.data.map(car => ({
            id: car.id,
            label: `${car.manufacturer} ${car.model}`,
            manufacturer: car.manufacturer,
            model: car.model
          }));
          console.log('Formatted cars:', formattedCars);
          setCars(formattedCars);
        }
      }

      // Fetch drivers
      const driversResponse = await fetch(`${API_URL}/api/admin/driver`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });
      if (driversResponse.ok) {
        const driversData = await driversResponse.json();
        console.log('Drivers data:', driversData);
        if (driversData.status === 1) {
          const formattedDrivers = driversData.data.map(driver => ({
            id: driver.id,
            label: `${driver.first_name} ${driver.last_name}`,
            first_name: driver.first_name,
            last_name: driver.last_name
          }));
          console.log('Formatted drivers:', formattedDrivers);
          setDrivers(formattedDrivers);
        }
      }

      // Fetch clients
      const clientsResponse = await fetch(`${API_URL}/api/admin/client`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });
      if (clientsResponse.ok) {
        const clientsData = await clientsResponse.json();
        console.log('Clients data:', clientsData);
        if (clientsData.status === 1) {
          const formattedClients = clientsData.data.map(client => ({
            id: client.id,
            label: `${client.first_name} ${client.last_name}`,
            first_name: client.first_name,
            last_name: client.last_name
          }));
          console.log('Formatted clients:', formattedClients);
          setClients(formattedClients);
        }
      }
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchJourneys = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }
      // Build query params
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('page', page);
      
      // Add filter parameters
      if (selectedCars.length > 0) {
        selectedCars.forEach(carId => params.append('car_id[]', carId));
      }
      if (selectedDrivers.length > 0) {
        selectedDrivers.forEach(driverId => params.append('driver_id[]', driverId));
      }
      if (selectedClients.length > 0) {
        selectedClients.forEach(clientId => params.append('client_id[]', clientId));
      }
      if (selectedStatus.length > 0) {
        selectedStatus.forEach(status => params.append('status[]', getStatusValue(status)));
      }
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (startTime) params.append('start_time', startTime);
      if (endTime) params.append('end_time', endTime);
      if (estimatedPriceMin) params.append('estimated_price_min', estimatedPriceMin);
      if (estimatedPriceMax) params.append('estimated_price_max', estimatedPriceMax);
      if (clientPriceMin) params.append('client_price_min', clientPriceMin);
      if (clientPriceMax) params.append('client_price_max', clientPriceMax);
      if (driverPriceMin) params.append('driver_price_min', driverPriceMin);
      if (driverPriceMax) params.append('driver_price_max', driverPriceMax);

      const url = `${API_URL}/api/admin/journey?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError("Oops, we haven't got JSON!");
      }
      const result = await response.json();
      if (result.status === 1) {
        setJourneys(result.data.data);
        setTotalPages(result.data.last_page);
      } else {
        throw new Error(result.message || 'Failed to fetch journeys');
      }
    } catch (error) {
      console.error('Error fetching journeys:', error);
      setJourneys([]);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetails = (journey) => {
    setSelectedJourney(journey);
    setShowDetails(true);
  };

  const handleJourneySelect = (journeyId) => {
    setSelectedJourneys(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(journeyId)) {
        newSelected.delete(journeyId);
        setSelectedJourneyPath(null);
      } else {
        newSelected.clear();
        newSelected.add(journeyId);
        // Find the selected journey and set its path
        const journey = journeys.find(j => j.id === journeyId);
        if (journey && journey.source_latitude && journey.source_longitude && 
            journey.destination_latitude && journey.destination_longitude) {
          setSelectedJourneyPath({
            id: journey.id,
            source: [journey.source_latitude, journey.source_longitude],
            destination: [journey.destination_latitude, journey.destination_longitude],
            title: `${journey.source_address} → ${journey.destination_address}`,
            supposed_path: journey.supposed_path,
            driver_arriving_path: journey.driver_arriving_path,
            taken_path: journey.taken_path
          });
        }
      }
      return newSelected;
    });

    // Find the selected journey
    const journey = journeys.find(j => j.id === journeyId);
    if (journey && journey.source_latitude && journey.source_longitude) {
      const position = [journey.source_latitude, journey.source_longitude];
      // Zoom to journey location in map
      if (mapRef.current) {
        mapRef.current.setView(position, 13);
      }
    }
  };

  const clearSelection = () => {
    setSelectedJourneys(new Set());
    setSelectedJourneyPath(null);
  };

  const handleMarkerClick = (journeyId) => {
    setHighlightedJourney(journeyId);
    // Find the row element and scroll it into view
    const rowElement = document.getElementById(`journey-row-${journeyId}`);
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Clear highlight after 2 seconds
    setTimeout(() => setHighlightedJourney(null), 2000);
  };

  const columns = [
    { name: "journey", align: "left" },
    { name: "status", align: "center" },
    { name: "client", align: "center" },
    { name: "car", align: "center" },
    { name: "driver", align: "center" },
    { name: "action", align: "center" },
  ];

  const rows = journeys.map((journey) => ({
    journey: (
      <VuiBox 
        id={`journey-row-${journey.id}`}
        display="flex" 
        alignItems="center" 
        px={1} 
        py={0.5}
        onClick={() => handleJourneySelect(journey.id)}
        sx={{
          cursor: 'pointer',
          background: selectedJourneys.has(journey.id) 
            ? 'rgba(33, 150, 243, 0.15)' 
            : highlightedJourney === journey.id 
              ? 'rgba(255, 193, 7, 0.15)' 
              : 'transparent',
          borderRadius: '8px',
          transition: 'background 0.2s',
          '&:hover': {
            background: selectedJourneys.has(journey.id) 
              ? 'rgba(33, 150, 243, 0.25)' 
              : highlightedJourney === journey.id 
                ? 'rgba(255, 193, 7, 0.25)' 
                : 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        <VuiBox display="flex" flexDirection="column">
          <VuiTypography variant="button" color="white" fontWeight="medium">
            {journey.source_address || 'Unknown Source'} → {journey.destination_address || 'Unknown Destination'}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            {journey.supposed_datetime ? new Date(journey.supposed_datetime).toLocaleString() : 'N/A'}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    ),
    status: (
      <VuiBadge
        variant="standard"
        badgeContent={formatStatus(journey.status)}
        color={
          journey.status === "Requested" ? "warning" :
          journey.status === "Accepted" ? "info" :
          journey.status === "Started" ? "primary" :
          journey.status === "Ended_by_Client" ? "success" :
          journey.status === "Ended_by_Driver" ? "success" :
          journey.status === "Cancelled" ? "error" :
          journey.status === "No_Driver_Found" ? "error" :
          journey.status === "Driver_Cancelled" ? "error" :
          journey.status === "Client_Cancelled" ? "error" :
          "info"
        }
        size="xs"
        container
        sx={({ palette: { white, success, error, warning, info, primary }, borders: { borderRadius, borderWidth } }) => ({
          background: 
            journey.status === "Requested" ? warning.main :
            journey.status === "Accepted" ? info.main :
            journey.status === "Started" ? primary.main :
            journey.status === "Ended_by_Client" ? success.main :
            journey.status === "Ended_by_Driver" ? success.main :
            journey.status === "Cancelled" ? error.main :
            journey.status === "No_Driver_Found" ? error.main :
            journey.status === "Driver_Cancelled" ? error.main :
            journey.status === "Client_Cancelled" ? error.main :
            info.main,
          border: `${borderWidth[1]} solid ${
            journey.status === "Requested" ? warning.main :
            journey.status === "Accepted" ? info.main :
            journey.status === "Started" ? primary.main :
            journey.status === "Ended_by_Client" ? success.main :
            journey.status === "Ended_by_Driver" ? success.main :
            journey.status === "Cancelled" ? error.main :
            journey.status === "No_Driver_Found" ? error.main :
            journey.status === "Driver_Cancelled" ? error.main :
            journey.status === "Client_Cancelled" ? error.main :
            info.main
          }`,
          borderRadius: borderRadius.md,
          color: white.main,
        })}
      />
    ),
    client: (
      <VuiBox display="flex" alignItems="center" justifyContent="center">
        {journey.client ? (
          <>
            <VuiAvatar 
              src={journey.client.profile_photo} 
              alt={`${journey.client.first_name} ${journey.client.last_name}`} 
              size="sm" 
              variant="rounded" 
            />
            <VuiBox ml={1}>
              <VuiTypography variant="caption" color="white">
                {journey.client.first_name} {journey.client.last_name}
              </VuiTypography>
            </VuiBox>
          </>
        ) : (
          <VuiTypography variant="caption" color="text">
            No client
          </VuiTypography>
        )}
      </VuiBox>
    ),
    car: (
      <VuiBox display="flex" alignItems="center" justifyContent="center">
        {journey.car ? (
          <>
            {journey.car.images && journey.car.images.length > 0 && (
              <VuiAvatar
                src={journey.car.images[0]}
                alt={journey.car.model || 'Car'}
                size="sm"
                variant="rounded"
                sx={{ mr: 1 }}
              />
            )}
            <VuiTypography variant="caption" color="white" mr={1}>
              {journey.car.manufacturer} {journey.car.model}
            </VuiTypography>
          </>
        ) : (
          <VuiTypography variant="caption" color="text">
            No car
          </VuiTypography>
        )}
      </VuiBox>
    ),
    driver: (
      <VuiBox display="flex" alignItems="center" justifyContent="center">
        {journey.driver ? (
          <>
            <VuiAvatar 
              src={journey.driver.profile_photo} 
              alt={`${journey.driver.first_name} ${journey.driver.last_name}`} 
              size="sm" 
              variant="rounded" 
            />
            <VuiBox ml={1}>
              <VuiTypography variant="caption" color="white">
                {journey.driver.first_name} {journey.driver.last_name}
              </VuiTypography>
            </VuiBox>
          </>
        ) : (
          <VuiTypography variant="caption" color="text">
            No driver
          </VuiTypography>
        )}
      </VuiBox>
    ),
    action: (
      <VuiBox display="flex" flexDirection="row" gap={1}>
        <FaEye
          style={{ color: "#1976d2", cursor: "pointer" }}
          title="Show Details"
          onClick={() => handleShowDetails(journey)}
        />
      </VuiBox>
    ),
  }));

  // Function to clear all filters
  const handleClearFilters = () => {
    setSelectedCars([]);
    setSelectedDrivers([]);
    setSelectedClients([]);
    setSelectedStatus([]);
    setStartDate('');
    setEndDate('');
    setStartTime('');
    setEndTime('');
    setEstimatedPriceMin('');
    setEstimatedPriceMax('');
    setClientPriceMin('');
    setClientPriceMax('');
    setDriverPriceMin('');
    setDriverPriceMax('');
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Card>
            {/* Top section with search bar and Filters button */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="22px" p={2}>
              <VuiBox display="flex" alignItems="center" gap={2}>
                <TextField
                  placeholder="Search journeys..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{ minWidth: 250 }}
                />
                <VuiButton
                  variant="contained"
                  color="info"
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ 
                    backgroundColor: '#1E2A49 !important', 
                    color: 'white !important', 
                    minWidth: 100
                  }}
                >
                  Filters
                </VuiButton>
                <VuiButton
                  variant="contained"
                  color="info"
                  onClick={() => setShowSettings(true)}
                  sx={{ 
                    backgroundColor: '#1E2A49 !important', 
                    color: 'white !important',
                    minWidth: 100,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <FaCog /> Settings
                </VuiButton>
              </VuiBox>
            </VuiBox>
            
            {/* Collapsible Filters section */}
            {
              showFilters && (
                <VuiBox p={2} pt={0}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <VuiBox
                      display="grid"
                      gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))"
                      gap={2}
                      mb={2}
                    >
                      {/* Cars Filter */}
                      <FormControl sx={{ minWidth: 220, width: 220, mb: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          color: '#333',
                          border: '1px solid #BDBDBD',
                          '& fieldset': { border: '1px solid #BDBDBD' },
                          '&:hover fieldset': { border: '1px solid #333' },
                          '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#333',
                          fontWeight: 400,
                          fontSize: 14,
                          top: '-3px',
                          background: 'transparent !important',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#fff',
                            background: 'transparent !important',
                            px: 0,
                            top: '-3px',
                          },
                          '&.MuiInputLabel-shrink': {
                            color: '#fff',
                            background: 'transparent !important',
                            top: '-6px',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#333',
                        },
                      }} size="small">
                        <InputLabel>Cars</InputLabel>
                        <Select
                          value={selectedCars[0] || ''}
                          onChange={e => setSelectedCars(e.target.value ? [e.target.value] : [])}
                          input={<OutlinedInput label="Cars" />}
                          displayEmpty
                          renderValue={selected => {
                            if (!selected) return <span style={{ color: '#888', fontStyle: 'italic' }}></span>;
                            const car = cars.find(c => c.id === selected);
                            return car ? `${car.manufacturer} ${car.model}` : '';
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: '#fff',
                                color: '#222',
                                boxShadow: 3,
                                mt: 1,
                                borderRadius: '0px',
                              },
                            },
                            MenuListProps: {
                              sx: {
                                padding: 0,
                              },
                            },
                          }}
                        >
                          <MenuItem value=""
                           sx={{
                            color: '#222',
                            fontWeight: 500,
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '0px',
                            '&.Mui-selected': {
                              backgroundColor: '#e0e0e0 !important',
                              borderRadius: '0px',
                            },
                            '&:hover': {
                              backgroundColor: '#f0f0f0 !important',
                              borderRadius: '0px',
                            },
                          }}>
                            <span style={{ color: '#2B2D3A', fontStyle: 'italic' }}>All</span>
                          </MenuItem>
                          {cars.map(car => (
                            <MenuItem key={car.id} value={car.id} sx={{
                                      color: '#222',
                                      fontWeight: 500,
                                      fontStyle: 'italic',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '0px',
                                      '&.Mui-selected': {
                                        backgroundColor: '#e0e0e0 !important',
                                        borderRadius: '0px',
                                      },
                                      '&:hover': {
                                        backgroundColor: '#f0f0f0 !important',
                                        borderRadius: '0px',
                                      },
                                    }}>
                              {car.manufacturer} {car.model}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {/* Drivers Filter */}
                      <FormControl sx={{ minWidth: 220, width: 220, mb: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          color: '#333',
                          border: '1px solid #BDBDBD',
                          '& fieldset': { border: '1px solid #BDBDBD' },
                          '&:hover fieldset': { border: '1px solid #333' },
                          '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#333',
                          fontWeight: 400,
                          fontSize: 14,
                          top: '-3px',
                          background: 'transparent !important',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#fff',
                            background: 'transparent !important',
                            px: 0,
                            top: '-6px',
                          },
                          '&.MuiInputLabel-shrink': {
                            color: '#fff',
                            background: 'transparent !important',
                            top: '-6px',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#333',
                        },
                      }} size="small">
                        <InputLabel>Drivers</InputLabel>
                        <Select
                          value={selectedDrivers[0] || ''}
                          onChange={e => setSelectedDrivers(e.target.value ? [e.target.value] : [])}
                          input={<OutlinedInput label="Drivers" />}
                          displayEmpty
                          renderValue={selected => {
                            if (!selected) return <span style={{ color: '#888', fontStyle: 'italic' }}></span>;
                            const driver = drivers.find(d => d.id === selected);
                            return driver ? `${driver.first_name} ${driver.last_name}` : '';
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: '#fff',
                                color: '#222',
                                boxShadow: 3,
                                mt: 1,
                                borderRadius: '0px',
                              },
                            },
                            MenuListProps: {
                              sx: {
                                padding: 0,
                              },
                            },
                          }}
                        >
                          <MenuItem value="" 
                                  sx={{
                                      color: '#222',
                                      fontWeight: 500,
                                      fontStyle: 'italic',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '0px',
                                      '&.Mui-selected': {
                                        backgroundColor: '#e0e0e0 !important',
                                        borderRadius: '0px',
                                      },
                                      '&:hover': {
                                        backgroundColor: '#f0f0f0 !important',
                                        borderRadius: '0px',
                                      },
                                    }}>
                            <span style={{ color: '#2B2D3A', fontStyle: 'italic' }}></span>
                          </MenuItem>
                          {drivers.map(driver => (
                            <MenuItem 
                            key={driver.id} 
                            value={driver.id} 
                            sx={{
                              borderRadius: '0px',
                              color: '#222',
                              backgroundColor: '#fff',
                              '&.Mui-selected': {
                                backgroundColor: '#e0e0e0 !important',
                                borderRadius: '0px',
                              },
                              '&:hover': {
                                backgroundColor: '#f0f0f0 !important',
                                borderRadius: '0px',
                              },
                            }}
                            >
                              {driver.first_name} {driver.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {/* Clients Filter */}
                      <FormControl sx={{ minWidth: 220, width: 220, mb: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          color: '#333',
                          border: '1px solid #BDBDBD',
                          '& fieldset': { border: '1px solid #BDBDBD' },
                          '&:hover fieldset': { border: '1px solid #333' },
                          '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#333',
                          fontWeight: 400,
                          fontSize: 14,
                          top: '-3px',
                          background: 'transparent !important',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#fff',
                            background: 'transparent !important',
                            px: 0,
                            top: '-3px',
                          },
                          '&.MuiInputLabel-shrink': {
                            color: '#fff',
                            background: 'transparent !important',
                            top: '-6px',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#333',
                        },
                      }} size="small">
                        <InputLabel>Clients</InputLabel>
                        <Select
                          value={selectedClients[0] || ''}
                          onChange={e => setSelectedClients(e.target.value ? [e.target.value] : [])}
                          input={<OutlinedInput label="Clients" />}
                          displayEmpty
                          renderValue={selected => {
                            if (!selected) return <span style={{ color: '#888', fontStyle: 'italic' }}></span>;
                            const client = clients.find(c => c.id === selected);
                            return client ? `${client.first_name} ${client.last_name}` : '';
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: '#fff',
                                color: '#222',
                                boxShadow: 3,
                                mt: 1,
                                borderRadius: '0px',
                              },
                            },
                            MenuListProps: {
                              sx: {
                                padding: 0,
                              },
                            },
                          }}
                        >
                          <MenuItem value=""
                           sx={{
                            color: '#222',
                            fontWeight: 500,
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '0px',
                            '&.Mui-selected': {
                              backgroundColor: '#e0e0e0 !important',
                              borderRadius: '0px',
                            },
                            '&:hover': {
                              backgroundColor: '#f0f0f0 !important',
                              borderRadius: '0px',
                            },
                          }}
                          >
                            <span style={{ color: '#2B2D3A', fontStyle: 'italic' }}></span>
                          </MenuItem>
                          {clients.map(client => (
                            <MenuItem key={client.id} value={client.id} 
                            sx={{
                              borderRadius: '0px',
                              color: '#222',
                              backgroundColor: '#fff',
                              '&.Mui-selected': {
                                backgroundColor: '#e0e0e0 !important',
                                borderRadius: '0px',
                              },
                              '&:hover': {
                                backgroundColor: '#f0f0f0 !important',
                                borderRadius: '0px',
                              },
                            }}
                            >
                              {client.first_name} {client.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {/* Status Filter */}
                      <FormControl sx={{ minWidth: 220, width: 220, mb: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          color: '#333',
                          border: '1px solid #BDBDBD',
                          '& fieldset': { border: '1px solid #BDBDBD' },
                          '&:hover fieldset': { border: '1px solid #333' },
                          '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#333',
                          fontWeight: 400,
                          fontSize: 14,
                          top: '-3px',
                          background: 'transparent !important',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#fff',
                            background: 'transparent !important',
                            px: 0,
                            top: '-3px',
                          },
                          '&.MuiInputLabel-shrink': {
                            color: '#fff',
                            background: 'transparent !important',
                            top: '-6px',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#333',
                        },
                      }} size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={selectedStatus[0] || ''}
                          onChange={e => setSelectedStatus(e.target.value ? [e.target.value] : [])}
                          input={<OutlinedInput label="Status" />}
                          displayEmpty
                          renderValue={selected => {
                            if (!selected) return <span style={{ color: '#888', fontStyle: 'italic' }}></span>;
                            return selected;
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: '#fff',
                                color: '#222',
                                boxShadow: 3,
                                mt: 1,
                                borderRadius: '0px',
                              },
                            },
                            MenuListProps: {
                              sx: {
                                padding: 0,
                              },
                            },
                          }}
                        >
                          <MenuItem value=""
                           sx={{
                            color: '#222',
                            fontWeight: 500,
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '0px',
                            '&.Mui-selected': {
                              backgroundColor: '#e0e0e0 !important',
                              borderRadius: '0px',
                            },
                            '&:hover': {
                              backgroundColor: '#f0f0f0 !important',
                              borderRadius: '0px',
                            },
                          }}
                          >
                            <span style={{ color: '#2B2D3A', fontStyle: 'italic' }}></span>
                          </MenuItem>
                          {[
                            "Requested",
                            "Scheduled",
                            "Approved By Client",
                            "Driver Arrived",
                            "Started",
                            "Ended by Client"
                          ].map(status => (
                            <MenuItem key={status} value={status} 
                            sx={{
                              borderRadius: '0px',
                              color: '#222',
                              backgroundColor: '#fff',
                              '&.Mui-selected': {
                                backgroundColor: '#e0e0e0 !important',
                                borderRadius: '0px',
                              },
                              '&:hover': {
                                backgroundColor: '#f0f0f0 !important',
                                borderRadius: '0px',
                              },
                            }}>
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {/* Start Date */}
                      <DatePicker
                        label="Start Date"
                        value={startDate ? new Date(startDate) : null}
                        onChange={date => setStartDate(date ? date.toISOString().slice(0, 10) : '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{
                              minWidth: 220,
                              width: 220,
                              mb: 0,
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                color: '#333',
                                border: '1px solid #BDBDBD',
                                '& fieldset': { border: '1px solid #BDBDBD' },
                                '&:hover fieldset': { border: '1px solid #333' },
                                '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                              },
                              '& .MuiInputLabel-root': {
                                color: '#333',
                                fontWeight: 400,
                                fontSize: 14,
                                top: '-3px',
                                background: 'transparent !important',
                                px: 0.5,
                                '&.Mui-focused': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  px: 0,
                                  top: '-3px',
                                },
                                '&.MuiInputLabel-shrink': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  top: '-6px',
                                },
                              },
                              '& .MuiSvgIcon-root': {
                                color: '#333',
                              },
                            }}
                          />
                        )}
                      />
                      {/* End Date */}
                      <DatePicker
                        label="End Date"
                        value={endDate ? new Date(endDate) : null}
                        onChange={date => setEndDate(date ? date.toISOString().slice(0, 10) : '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{
                              minWidth: 220,
                              width: 220,
                              mb: 0,
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                color: '#333',
                                border: '1px solid #BDBDBD',
                                '& fieldset': { border: '1px solid #BDBDBD' },
                                '&:hover fieldset': { border: '1px solid #333' },
                                '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                              },
                              '& .MuiInputLabel-root': {
                                color: '#333',
                                fontWeight: 400,
                                fontSize: 14,
                                top: '-3px',
                                background: 'transparent !important',
                                px: 0.5,
                                '&.Mui-focused': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  px: 0,
                                  top: '-3px',
                                },
                                '&.MuiInputLabel-shrink': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  top: '-6px',
                                },
                              },
                              '& .MuiSvgIcon-root': {
                                color: '#333',
                              },
                            }}
                          />
                        )}
                      />
                      {/* Start Time */}
                      <TimePicker
                        label="Start Time"
                        value={startTime ? new Date(`1970-01-01T${startTime}`) : null}
                        onChange={date => setStartTime(date ? date.toTimeString().slice(0, 5) : '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{
                              minWidth: 220,
                              width: 220,
                              mb: 0,
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                color: '#333',
                                border: '1px solid #BDBDBD',
                                '& fieldset': { border: '1px solid #BDBDBD' },
                                '&:hover fieldset': { border: '1px solid #333' },
                                '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                              },
                              '& .MuiInputLabel-root': {
                                color: '#333',
                                fontWeight: 400,
                                fontSize: 14,
                                top: '-3px',
                                background: 'transparent !important',
                                px: 0.5,
                                '&.Mui-focused': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  px: 0,
                                  top: '-3px',
                                },
                                '&.MuiInputLabel-shrink': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  top: '-6px',
                                },
                              },
                              '& .MuiSvgIcon-root': {
                                color: '#333',
                              },
                            }}
                          />
                        )}
                      />
                      {/* End Time */}
                      <TimePicker
                        label="End Time"
                        value={endTime ? new Date(`1970-01-01T${endTime}`) : null}
                        onChange={date => setEndTime(date ? date.toTimeString().slice(0, 5) : '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{
                              minWidth: 220,
                              width: 220,
                              mb: 0,
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                color: '#333',
                                border: '1px solid #BDBDBD',
                                '& fieldset': { border: '1px solid #BDBDBD' },
                                '&:hover fieldset': { border: '1px solid #333' },
                                '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                              },
                              '& .MuiInputLabel-root': {
                                color: '#333',
                                fontWeight: 400,
                                fontSize: 14,
                                top: '-3px',
                                background: 'transparent !important',
                                px: 0.5,
                                '&.Mui-focused': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  px: 0,
                                  top: '-3px',
                                },
                                '&.MuiInputLabel-shrink': {
                                  color: '#fff',
                                  background: 'transparent !important',
                                  top: '-6px',
                                },
                              },
                              '& .MuiSvgIcon-root': {
                                color: '#333',
                              },
                            }}
                          />
                        )}
                      />
                      {/* Min Estimated Price */}
                      <TextField
                        label="Min Estimated Price"
                        type="number"
                        value={estimatedPriceMin}
                        onChange={(e) => setEstimatedPriceMin(e.target.value)}
                        size="small"
                        sx={{
                          minWidth: 220,
                          width: 220,
                          mb: 0,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            color: '#333',
                            border: '1px solid #BDBDBD',
                            '& fieldset': { border: '1px solid #BDBDBD' },
                            '&:hover fieldset': { border: '1px solid #333' },
                            '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#333',
                            fontWeight: 400,
                            fontSize: 14,
                            top: '-3px',
                            background: 'transparent !important',
                            px: 0.5,
                            '&.Mui-focused': {
                              color: '#fff',
                              background: 'transparent !important',
                              px: 0,
                              top: '-3px',
                            },
                            '&.MuiInputLabel-shrink': {
                              color: '#fff',
                              background: 'transparent !important',
                              top: '-6px',
                            },
                          },
                        }}
                      />
                      {/* Max Estimated Price */}
                      <TextField
                        label="Max Estimated Price"
                        type="number"
                        value={estimatedPriceMax}
                        onChange={(e) => setEstimatedPriceMax(e.target.value)}
                        size="small"
                        sx={{
                          minWidth: 220,
                          width: 220,
                          mb: 0,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            color: '#333',
                            border: '1px solid #BDBDBD',
                            '& fieldset': { border: '1px solid #BDBDBD' },
                            '&:hover fieldset': { border: '1px solid #333' },
                            '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#333',
                            fontWeight: 400,
                            fontSize: 14,
                            top: '-3px',
                            background: 'transparent !important',
                            px: 0.5,
                            '&.Mui-focused': {
                              color: '#fff',
                              background: 'transparent !important',
                              px: 0,
                              top: '-3px',
                            },
                            '&.MuiInputLabel-shrink': {
                              color: '#fff',
                              background: 'transparent !important',
                              top: '-6px',
                              left: '-9px'
                            },
                          },
                        }}
                      />
                      {/* Min Client Price */}
                      <TextField
                        label="Min Client Price"
                        type="number"
                        value={clientPriceMin}
                        onChange={(e) => setClientPriceMin(e.target.value)}
                        size="small"
                        sx={{
                          minWidth: 220,
                          width: 220,
                          mb: 0,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            color: '#333',
                            border: '1px solid #BDBDBD',
                            '& fieldset': { border: '1px solid #BDBDBD' },
                            '&:hover fieldset': { border: '1px solid #333' },
                            '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#333',
                            fontWeight: 400,
                            fontSize: 14,
                            top: '-3px',
                            background: 'transparent !important',
                            px: 0.5,
                            '&.Mui-focused': {
                              color: '#fff',
                              background: 'transparent !important',
                              px: 0,
                              top: '-3px',
                            },
                            '&.MuiInputLabel-shrink': {
                              color: '#fff',
                              background: 'transparent !important',
                              top: '-6px',
                              left: '-9px'
                            },
                          },
                        }}
                      />
                      {/* Max Client Price */}
                      <TextField
                        label="Max Client Price"
                        type="number"
                        value={clientPriceMax}
                        onChange={(e) => setClientPriceMax(e.target.value)}
                        size="small"
                        sx={{
                          minWidth: 220,
                          width: 220,
                          mb: 0,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            color: '#333',
                            border: '1px solid #BDBDBD',
                            '& fieldset': { border: '1px solid #BDBDBD' },
                            '&:hover fieldset': { border: '1px solid #333' },
                            '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#333',
                            fontWeight: 400,
                            fontSize: 14,
                            top: '-3px',
                            background: 'transparent !important',
                            px: 0.5,
                            '&.Mui-focused': {
                              color: '#fff',
                              background: 'transparent !important',
                              px: 0,
                              top: '-3px',
                            },
                            '&.MuiInputLabel-shrink': {
                              color: '#fff',
                              background: 'transparent !important',
                              top: '-6px',
                              left: '-9px'
                            },
                          },
                        }}
                      />
                      {/* Min Driver Price */}
                      <TextField
                        label="Min Driver Price"
                        type="number"
                        value={driverPriceMin}
                        onChange={(e) => setDriverPriceMin(e.target.value)}
                        size="small"
                        sx={{
                          minWidth: 220,
                          width: 220,
                          mb: 0,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            color: '#333',
                            border: '1px solid #BDBDBD',
                            '& fieldset': { border: '1px solid #BDBDBD' },
                            '&:hover fieldset': { border: '1px solid #333' },
                            '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#333',
                            fontWeight: 400,
                            fontSize: 14,
                            top: '-3px',
                            background: 'transparent !important',
                            px: 0.5,
                            '&.Mui-focused': {
                              color: '#fff',
                              background: 'transparent !important',
                              px: 0,
                              top: '-3px',
                            },
                            '&.MuiInputLabel-shrink': {
                              color: '#fff',
                              background: 'transparent !important',
                              top: '-6px',
                            },
                          },
                        }}
                      />
                      {/* Max Driver Price */}
                      <TextField
                        label="Max Driver Price"
                        type="number"
                        value={driverPriceMax}
                        onChange={(e) => setDriverPriceMax(e.target.value)}
                        size="small"
                        sx={{
                          minWidth: 220,
                          width: 220,
                          mb: 0,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            color: '#333',
                            border: '1px solid #BDBDBD',
                            '& fieldset': { border: '1px solid #BDBDBD' },
                            '&:hover fieldset': { border: '1px solid #333' },
                            '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#333',
                            fontWeight: 400,
                            fontSize: 14,
                            top: '-3px',
                            background: 'transparent !important',
                            px: 0.5,
                            '&.Mui-focused': {
                              color: '#fff',
                              background: 'transparent !important',
                              px: 0,
                              top: '-3px',
                            },
                            '&.MuiInputLabel-shrink': {
                              color: '#fff',
                              background: 'transparent !important',
                              top: '-6px',
                            },
                          },
                        }}
                      />
                    </VuiBox>
                  </LocalizationProvider>
                  {/* Clear Filters Button */}
                  <VuiBox display="flex" justifyContent="flex-end">
                    <Button 
                      variant="text" 
                      onClick={handleClearFilters} 
                      sx={{ color: 'white !important' }}
                    >
                      Clear Filters
                    </Button>
                  </VuiBox>
                </VuiBox>
              )
            }
          </Card>
        </VuiBox>
        <VuiBox mb={3} flex={1} display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={3}>
          {/* Journey Table Card */}
          <Card sx={{ flex: 2, background: '#191932', borderRadius: '18px', overflow: 'hidden' }}>
            <VuiBox p={3}>
              <VuiTypography variant="h5" color="white" fontWeight="bold" mb={2}>
                Journeys
              </VuiTypography>

              {loading ? (
                <VuiBox display="flex" justifyContent="center" p={3}>
                  <VuiTypography variant="button" color="white">
                    Loading...
                  </VuiTypography>
                </VuiBox>
              ) : journeys.length === 0 ? (
                <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={5}>
                  <img src={notFoundImg} alt="Not found" style={{ maxWidth: 180, opacity: 0.7, marginBottom: 16 }} />
                  <VuiTypography variant="button" color="text">
                    No journeys found
                  </VuiTypography>
                </VuiBox>
              ) : (
                <VuiBox
                  sx={{
                    "& th": {
                      borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                        `${borderWidth[1]} solid ${grey[700]}`,
                      position: 'sticky',
                      top: 0,
                      background: '#191932',
                      zIndex: 2,
                    },
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                          `${borderWidth[1]} solid ${grey[700]}`,
                      },
                    },
                    width: '100%',
                  }}
                >
                  <Table columns={columns} rows={rows} TableContainerProps={{ sx: { maxHeight: '390px' } }} />
                </VuiBox>
              )}

              {/* Pagination */}
              <VuiBox display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#fff',
                    },
                    '& .Mui-selected': {
                      background: '#2196f3 !important',
                    },
                  }}
                />
              </VuiBox>
            </VuiBox>
          </Card>

          {/* Map Card */}
          <Card sx={{ flex: 1, background: '#191932', borderRadius: '18px', overflow: 'hidden' }}>
            <VuiBox p={3}>
              <VuiTypography variant="h5" color="white" fontWeight="bold" mb={2}>
                Journey Map
              </VuiTypography>
              {/* Path toggles */}
              <VuiBox display="flex" gap={2} alignItems="center" mb={2}>
                <FormControlLabel
                  control={<Checkbox checked={showPaths.supposed} onChange={e => setShowPaths(s => ({ ...s, supposed: e.target.checked }))} sx={{ color: '#888' }} />}
                  label={<span style={{ color: '#888' }}>Supposed Path</span>}
                />
                <FormControlLabel
                  control={<Checkbox checked={showPaths.arriving} onChange={e => setShowPaths(s => ({ ...s, arriving: e.target.checked }))} sx={{ color: '#2196f3' }} />}
                  label={<span style={{ color: '#2196f3' }}>Driver Arriving Path</span>}
                />
                <FormControlLabel
                  control={<Checkbox checked={showPaths.taken} onChange={e => setShowPaths(s => ({ ...s, taken: e.target.checked }))} sx={{ color: '#43a047' }} />}
                  label={<span style={{ color: '#43a047' }}>Taken Path</span>}
                />
              </VuiBox>
              <Box sx={{ 
                width: '100%', 
                position: 'relative',
                aspectRatio: '1/1',
                maxWidth: { xs: '100%', lg: '600px' },
                margin: '0 auto'
              }}>
                <MapContainer
                  center={[33.5138, 36.2765]}
                  zoom={12}
                  style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                  ref={mapRef}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {journeys.map((journey) => (
                    journey.source_latitude && 
                    journey.source_longitude && 
                    (selectedJourneys.size === 0 || selectedJourneys.has(journey.id)) && (
                      <React.Fragment key={journey.id}>
                        <Marker
                          position={[journey.source_latitude, journey.source_longitude]}
                          eventHandlers={{
                            click: () => handleMarkerClick(journey.id)
                          }}
                        >
                          <Popup>
                            <VuiBox>
                              <VuiTypography variant="button" color="text" fontWeight="bold">
                                {journey.source_address || 'Unknown Source'} → {journey.destination_address || 'Unknown Destination'}
                              </VuiTypography>
                              <VuiTypography variant="caption" color="text">
                                {journey.supposed_datetime ? new Date(journey.supposed_datetime).toLocaleString() : 'N/A'}
                              </VuiTypography>
                            </VuiBox>
                          </Popup>
                        </Marker>
                        {journey.destination_latitude && journey.destination_longitude && (
                          <Marker
                            position={[journey.destination_latitude, journey.destination_longitude]}
                          >
                            <Popup>
                              <VuiBox>
                                <VuiTypography variant="button" color="text" fontWeight="bold">
                                  Destination
                                </VuiTypography>
                                <VuiTypography variant="caption" color="text">
                                  {journey.destination_address}
                                </VuiTypography>
                              </VuiBox>
                            </Popup>
                          </Marker>
                        )}
                      </React.Fragment>
                    )
                  ))}
                  {selectedJourneyPath && (
                    <>
                      {/* Supposed Path (grey) */}
                      {showPaths.supposed && selectedJourneyPath.supposed_path && (
                        <Polyline
                          positions={selectedJourneyPath.supposed_path}
                          pathOptions={{
                            color: '#888',
                            weight: 3,
                            opacity: 0.8
                          }}
                        />
                      )}
                      {/* Driver Arriving Path (blue) */}
                      {showPaths.arriving && selectedJourneyPath.driver_arriving_path && (
                        <Polyline
                          positions={selectedJourneyPath.driver_arriving_path}
                          pathOptions={{
                            color: '#2196f3',
                            weight: 3,
                            opacity: 0.8
                          }}
                        />
                      )}
                      {/* Taken Path (green) */}
                      {showPaths.taken && selectedJourneyPath.taken_path && (
                        <Polyline
                          positions={selectedJourneyPath.taken_path}
                          pathOptions={{
                            color: '#43a047',
                            weight: 3,
                            opacity: 0.8
                          }}
                        />
                      )}
                    </>
                  )}
                </MapContainer>
              </Box>
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>

      {/* Journey Details Modal */}
      {selectedJourney && (
        <JourneyDetailsModal
          open={showDetails}
          onClose={() => {
            setShowDetails(false);
            setSelectedJourney(null);
          }}
          journey={selectedJourney}
        />
      )}

      {/* Journey Settings Modal */}
      <JourneySettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Journey; 